import { AI_MODELS, getDefaultModelForTier, type AIModelTechnicalName, type AIModelTier } from '@my-roadtrip/types'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('AIModelSelector')

/**
 * Determines which AI model to use based on user's plan tier and optional preference
 */
export class AIModelSelector {
  /**
   * Select appropriate AI model for a request
   * 
   * @param userTier - User's plan tier (standard, premium, pro)
   * @param preferredModel - Optional user preference (must be allowed by tier)
   * @param fallbackModel - Fallback if preference not available
   * @returns Technical model name to use
   */
  static selectModel(
    userTier: AIModelTier = 'standard',
    preferredModel?: AIModelTechnicalName,
    fallbackModel?: AIModelTechnicalName
  ): AIModelTechnicalName {
    // If user has a preference, validate it's allowed by their tier
    if (preferredModel) {
      const modelConfig = AI_MODELS[preferredModel]
      
      if (!modelConfig) {
        logger.warn('Unknown model requested', { preferredModel, userTier })
      } else if (!modelConfig.enabled || modelConfig.deprecated) {
        logger.warn('Disabled/deprecated model requested', { preferredModel, userTier })
      } else if (this.isTierAllowed(userTier, modelConfig.requiredTier)) {
        logger.info('Using user-preferred model', { model: preferredModel, userTier })
        return preferredModel
      } else {
        logger.warn('User requested model above their tier', {
          preferredModel,
          userTier,
          requiredTier: modelConfig.requiredTier
        })
      }
    }

    // Try fallback model
    if (fallbackModel) {
      const fallbackConfig = AI_MODELS[fallbackModel]
      if (
        fallbackConfig &&
        fallbackConfig.enabled &&
        !fallbackConfig.deprecated &&
        this.isTierAllowed(userTier, fallbackConfig.requiredTier)
      ) {
        logger.info('Using fallback model', { model: fallbackModel, userTier })
        return fallbackModel
      }
    }

    // Use tier default
    const defaultModel = getDefaultModelForTier(userTier)
    logger.info('Using tier default model', { model: defaultModel, userTier })
    return defaultModel
  }

  /**
   * Get model from environment variable with tier validation
   */
  static selectFromEnv(userTier: AIModelTier = 'standard'): AIModelTechnicalName {
    const envModel = process.env.OPENAI_MODEL as AIModelTechnicalName | undefined
    return this.selectModel(userTier, envModel)
  }

  /**
   * Check if a user tier allows access to a model tier
   */
  private static isTierAllowed(userTier: AIModelTier, requiredTier: AIModelTier): boolean {
    const hierarchy: Record<AIModelTier, number> = {
      standard: 1,
      premium: 2,
      pro: 3
    }

    return hierarchy[userTier] >= hierarchy[requiredTier]
  }

  /**
   * Get display name for a technical model name
   */
  static getDisplayName(technicalName: string): string {
    const model = AI_MODELS[technicalName as AIModelTechnicalName]
    return model?.displayName ?? technicalName
  }

  /**
   * Get cost multiplier for a model
   */
  static getCostMultiplier(technicalName: string): number {
    const model = AI_MODELS[technicalName as AIModelTechnicalName]
    return model?.costMultiplier ?? 1
  }
}
